import { useEffect, useState } from 'preact/hooks';
import { type Language, Prefs, stringsFor } from '../app/prefs';
import { BrandMark, PlainButton, PrimaryButton, Sheet } from './components';
import { KEY_POINT_ICONS, Share } from './icons';
import { ui, useApp } from './state';

export function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone === true;
}

export function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent);
}

// Android/Chrome can install directly: keep the browser's install prompt for a button.
let installPrompt: any = null;
const promptListeners = new Set<() => void>();
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  installPrompt = e;
  promptListeners.forEach((f) => f());
});

/** First screen in a browser (before the language): how to add COINWOW to the Home Screen. */
export function InstallFirst() {
  const [lang, setLang] = useState<Language>(Prefs.currentLanguage());
  const [canPrompt, setCanPrompt] = useState(!!installPrompt);
  useEffect(() => {
    const f = () => setCanPrompt(!!installPrompt);
    promptListeners.add(f);
    return () => { promptListeners.delete(f); };
  }, []);
  const s = stringsFor(lang);
  const done = () => { Prefs.dismissInstall(); ui.dispatchEvent(new Event('change')); };
  const sections = [
    { id: 'ios', title: s.installIPhoneTitle, steps: s.installIPhoneSteps },
    { id: 'android', title: s.installAndroidTitle, steps: s.installAndroidSteps },
  ];
  if (isAndroid()) sections.reverse();
  return (
    <div class="screen">
      <div class="lang-switch" role="group" aria-label="Langue / Language">
        {(['fr', 'en'] as Language[]).map((l) => (
          <button type="button" class={`lang-btn ${lang === l ? "on" : ""}`} aria-pressed={lang === l} onClick={() => setLang(l)}>{l.toUpperCase()}</button>
        ))}
      </div>
      <div class="stack-20 grow">
        <BrandMark size={72} />
        <h1 class="large-title">{s.installFirstTitle}</h1>
        <p class="dim">{s.installFirstText}</p>
        {canPrompt && (
          <PrimaryButton onClick={async () => {
            const p = installPrompt;
            installPrompt = null;
            setCanPrompt(false);
            await p?.prompt?.();
          }}>{s.installNow}</PrimaryButton>
        )}
        {sections.map((sec) => (
          <div class="card pad stack-10">
            <div class="headline">{sec.title}{sec.id === 'ios' && <> <Share size={16} /></>}</div>
            <ol class="steps">
              {sec.steps.map((t, i) => <li><span class="step-num">{i + 1}</span><span>{t}</span></li>)}
            </ol>
          </div>
        ))}
        <p class="strong small">{s.installIOSNote}</p>
        <p class="dim small">{s.storageWarning}</p>
        <PlainButton class="strong" onClick={done}>{s.installLater}</PlainButton>
      </div>
    </div>
  );
}

export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** Explains how to add COINWOW to the Home Screen (the web app then behaves like an app). */
export function InstallSheet() {
  const { s } = useApp();
  const steps = isIOS() ? s.installStepsIOS : s.installStepsOther;
  const done = () => { Prefs.dismissInstall(); ui.close(); };
  return (
    <Sheet right={<PlainButton onClick={done}>{s.later}</PlainButton>}>
      <div class="stack-20 grow">
        <div class="spacer" />
        <BrandMark size={72} />
        <h1 class="large-title">{s.installTitle}</h1>
        <p class="dim">{s.installText}</p>
        <ol class="steps">
          {steps.map((t, i) => <li><span class="step-num">{i + 1}</span><span>{t}{i === 0 && isIOS() && <> <Share size={16} /></>}</span></li>)}
        </ol>
        {isIOS() && <p class="strong small">{s.installIOSNote}</p>}
        <p class="dim small">{s.storageWarning}</p>
        <div class="spacer" />
        <PrimaryButton onClick={done}>{s.installLater}</PrimaryButton>
      </div>
    </Sheet>
  );
}

/** The web-specific risks, shown once after the wallet opens and anytime in Settings. */
export function WebWarningsSheet() {
  const { s } = useApp();
  const done = () => { Prefs.setWebWarningsSeen(); ui.close(); };
  return (
    <Sheet title={s.webWarningsTitle} right={<PlainButton onClick={done}>{s.done}</PlainButton>}>
      <div class="stack-20">
        <p class="dim">{s.webWarningsIntro}</p>
        <div class="card pad stack-18">
          {s.webWarnings.map((p) => {
            const Icon = KEY_POINT_ICONS[p.icon];
            return (
              <div class="keypoint">
                <span class="icon-circle"><Icon size={18} strokeWidth={2.4} /></span>
                <div>
                  <div class="headline">{p.title}</div>
                  <div class="dim small">{p.detail}</div>
                </div>
              </div>
            );
          })}
        </div>
        <PrimaryButton onClick={done}>{s.webWarningsAccept}</PrimaryButton>
      </div>
    </Sheet>
  );
}
